import { useMemo, useState } from 'react';
import { Wallet, TrendingUp, TrendingDown, AlertCircle, RefreshCw } from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';
import useLocalState from '../hooks/useLocalState.js';
import { fairValueDistancePct, powerLawFairValue } from '../lib/powerLaw.js';
import { formatUsd, formatBtc, formatPct } from '../lib/format.js';

// 30-day P&L from monthly Yahoo closes — the underlying series is monthly
// granularity, so the "30 day" window is approximate (one monthly bar
// back). Honest label given the data we have; daily granularity would
// require a different proxy source we don't run yet.
function compute30dChange(history) {
  if (!history || history.length < 2) return null;
  const last = history[history.length - 1]?.value;
  const prior = history[history.length - 2]?.value;
  if (!Number.isFinite(last) || !Number.isFinite(prior) || prior <= 0) return null;
  return ((last / prior) - 1) * 100;
}

function CardShell({ children }) {
  return (
    <div className="rounded-2xl bg-stone-900/60 border border-stone-800 p-6 hover:border-amber-500/40 transition-colors">
      {children}
    </div>
  );
}

function StatusPill({ status, error }) {
  if (status === 'live')
    return <span className="text-[10px] uppercase tracking-wider font-mono text-emerald-400/80 bg-emerald-400/10 px-2 py-0.5 rounded">Live</span>;
  if (status === 'refreshing')
    return <span className="text-[10px] uppercase tracking-wider font-mono text-amber-400/80 bg-amber-400/10 px-2 py-0.5 rounded">Refreshing</span>;
  if (status === 'stale')
    return <span className="text-[10px] uppercase tracking-wider font-mono text-orange-400/80 bg-orange-400/10 px-2 py-0.5 rounded" title={error || ''}>Stale</span>;
  if (status === 'error')
    return <span className="text-[10px] uppercase tracking-wider font-mono text-red-400/80 bg-red-400/10 px-2 py-0.5 rounded" title={error || ''}>Error</span>;
  return <span className="text-[10px] uppercase tracking-wider font-mono text-stone-500 bg-stone-500/10 px-2 py-0.5 rounded">Loading</span>;
}

export default function MyStack() {
  const [stack, setStack] = useLocalState('myStack', { btc: 0 }, 1);
  const [draft, setDraft] = useState(String(stack.btc || ''));
  const [editing, setEditing] = useState(stack.btc === 0);

  const { btc, refetch } = useMarketData();

  const usdValue = useMemo(() => {
    if (!Number.isFinite(btc.spot) || !Number.isFinite(stack.btc)) return null;
    return btc.spot * stack.btc;
  }, [btc.spot, stack.btc]);

  const fairValue = useMemo(() => powerLawFairValue(new Date()), []);
  const fairValueStackUsd = useMemo(() => {
    if (!Number.isFinite(stack.btc) || !Number.isFinite(fairValue)) return null;
    return fairValue * stack.btc;
  }, [stack.btc, fairValue]);

  const fairValuePct = useMemo(
    () => fairValueDistancePct(btc.spot, new Date()),
    [btc.spot],
  );

  const change30d = useMemo(() => compute30dChange(btc.history), [btc.history]);

  function commitDraft(e) {
    e?.preventDefault();
    const parsed = parseFloat(draft);
    const clean = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    setStack({ btc: clean });
    setEditing(false);
  }

  return (
    <CardShell>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
            <Wallet size={20} className="text-stone-900" />
          </div>
          <h3 className="font-display text-lg font-semibold text-cream">My Stack</h3>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={btc.status} error={btc.error} />
          <button
            onClick={refetch}
            disabled={btc.status === 'loading' || btc.status === 'refreshing'}
            className="p-1.5 rounded-md text-stone-400 hover:text-amber-400 hover:bg-stone-800/60 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw size={14} className={btc.status === 'refreshing' ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Holdings input / display */}
      {editing ? (
        <form onSubmit={commitDraft} className="mb-5">
          <label className="block text-xs uppercase tracking-wider text-stone-500 mb-2">
            Your holdings
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="decimal"
              step="0.00000001"
              min="0"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              placeholder="0.00000000"
              className="flex-1 bg-stone-950/60 border border-stone-700 rounded-lg px-3 py-2 text-cream font-mono tabular-nums focus:outline-none focus:border-amber-500/60"
            />
            <span className="self-center font-mono text-sm text-stone-500">BTC</span>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-gradient-to-br from-orange-500 to-amber-400 text-stone-900 font-semibold text-sm hover:brightness-110 transition"
            >
              Save
            </button>
          </div>
          <p className="text-xs text-stone-600 mt-2">
            Stored in your browser only. Never sent to any server.
          </p>
        </form>
      ) : (
        <div className="mb-5">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Your holdings</div>
          <div className="flex items-baseline justify-between">
            <div className="font-mono tabular-nums text-2xl text-cream">{formatBtc(stack.btc)}</div>
            <button
              onClick={() => { setDraft(String(stack.btc)); setEditing(true); }}
              className="text-xs text-amber-400/80 hover:text-amber-400 font-mono"
            >
              Edit
            </button>
          </div>
        </div>
      )}

      {/* USD value + spot */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">USD value</div>
          <div className="font-mono tabular-nums text-xl text-cream">
            {usdValue != null ? formatUsd(usdValue) : <span className="text-stone-600">—</span>}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">BTC spot</div>
          <div className="font-mono tabular-nums text-xl text-cream">
            {btc.spot != null ? formatUsd(btc.spot) : <span className="text-stone-600">—</span>}
          </div>
        </div>
      </div>

      {/* Fair-value distance + 30d change */}
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div className="rounded-lg bg-stone-950/40 p-3">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
            Fair-value gap
          </div>
          <div className={`font-mono tabular-nums text-lg ${
            fairValuePct == null ? 'text-stone-600' :
            fairValuePct >= 0 ? 'text-amber-400' : 'text-emerald-400'
          }`}>
            {fairValuePct != null ? formatPct(fairValuePct, { signed: true }) : '—'}
          </div>
          <div className="text-[10px] text-stone-600 mt-1">
            {fairValuePct == null ? 'awaiting price'
              : fairValuePct >= 0 ? 'spot above power-law fair value'
              : 'spot below power-law fair value'}
          </div>
        </div>

        <div className="rounded-lg bg-stone-950/40 p-3">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">30-day change</div>
          <div className={`font-mono tabular-nums text-lg flex items-center gap-1 ${
            change30d == null ? 'text-stone-600' :
            change30d >= 0 ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {change30d == null ? '—' : (
              <>
                {change30d >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                {formatPct(change30d, { signed: true })}
              </>
            )}
          </div>
          <div className="text-[10px] text-stone-600 mt-1">approx. — monthly close</div>
        </div>
      </div>

      {/* Fair-value reference (informational, not a price target) */}
      {Number.isFinite(stack.btc) && stack.btc > 0 && fairValueStackUsd != null && (
        <div className="text-[11px] text-stone-500 mt-3 leading-relaxed">
          At today's power-law fair value ({formatUsd(fairValue)}), your stack would be worth{' '}
          <span className="text-stone-400 font-mono tabular-nums">{formatUsd(fairValueStackUsd)}</span>.
          Educational reference only — not a forecast.
        </div>
      )}

      {/* Error state */}
      {btc.status === 'error' && (
        <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/30 p-3 flex items-start gap-2">
          <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-300/90">
            Couldn't load BTC spot. {btc.error || 'Check your connection or try again in a moment.'}
          </div>
        </div>
      )}
    </CardShell>
  );
}
